import { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Car,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileDown,
  ListChecks,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Shared/Button';
import { Card } from '@/components/Shared/Card';
import EmptyState from '@/components/Shared/EmptyState';
import Badge from '@/components/Shared/Badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import { useMyPlanStore } from '@/store/useMyPlanStore';
import { useMyChecklistStore, type TaskChecklistGroup } from '@/store/useMyChecklistStore';
import { TaskChecklistPanel } from '@/components/Dashboard/Employee/TaskChecklistPanel';
import { useReportDownload } from '@/hooks/useReportDownload';
import type { FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryListDto as PlanningEntryListDto } from '@/api/generated/models';
import type { FacilityFlowCoreModulesPlanningDtosPlanningEntriesPlanningEntryTaskDto as PlanningEntryTaskDto } from '@/api/generated/models';
import { addIsoDays, isoDate, parseIsoDate } from '@/utils/dateUtils';

function formatDisplayDate(dateStr: string, locale: string): string {
  const d = parseIsoDate(dateStr);
  return d.toLocaleDateString(
    locale === 'de' ? 'de-AT' : locale === 'sl' ? 'sl-SI' : 'en-US',
    { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' },
  );
}

function todayString(): string {
  return isoDate(new Date());
}

function formatTime(value?: string | null): string {
  if (!value) return '–';
  return value.substring(0, 5);
}

export default function MyPlanPage() {
  const { t, i18n } = useTranslation('myplan');
  const { entries, loading, error, currentDate, fetchMyPlan, setCurrentDate } = useMyPlanStore();
  const { downloading, download } = useReportDownload();
  const fetchChecklistDay = useMyChecklistStore((s) => s.fetchDay);
  const checklistDays = useMyChecklistStore((s) => s.days);

  const loadDay = useCallback(
    (date: string) => {
      setCurrentDate(date);
      fetchMyPlan(date, date);
      fetchChecklistDay(date);
    },
    [setCurrentDate, fetchMyPlan, fetchChecklistDay],
  );

  useEffect(() => {
    loadDay(currentDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isToday = currentDate === todayString();
  // Only the future is read-only — leftover items of past days stay confirmable (ADR-0017).
  const isFuture = currentDate > todayString();

  const checklistsByTaskId = useMemo(() => {
    const map: Record<string, TaskChecklistGroup> = {};
    (checklistDays[currentDate]?.tasks ?? []).forEach((group) => {
      if (group.taskId) map[group.taskId] = group;
    });
    return map;
  }, [checklistDays, currentDate]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="secondary"
              onClick={() => download('daily', { date: currentDate })}
              disabled={downloading}
              leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('actions.downloadPdf')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => download('daily', { date: currentDate, includeChecklists: true })}
              disabled={downloading}
              leftIcon={<ListChecks className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('actions.downloadPdfWithChecklists')}
            </Button>
          </div>
        </div>
      </div>

      {/* Day picker / nav */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => loadDay(addIsoDays(currentDate, -1))}
            title={t('actions.previous')}
            aria-label={t('actions.previous')}
            className="p-1.5"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </Button>
          <input
            type="date"
            value={currentDate}
            onChange={(e) => e.target.value && loadDay(e.target.value)}
            aria-label={t('fields.date')}
            className="px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => loadDay(addIsoDays(currentDate, 1))}
            title={t('actions.next')}
            aria-label={t('actions.next')}
            className="p-1.5"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </Button>
          {!isToday && (
            <Button variant="secondary" size="sm" onClick={() => loadDay(todayString())}>
              {t('actions.today')}
            </Button>
          )}
          <span className="text-[13px] text-slate-500 ml-1 tabular-nums truncate">
            {formatDisplayDate(currentDate, i18n.language)}
          </span>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#00A871]" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <EmptyState
          variant="error"
          title={t('errors.fetchFailed')}
          onRetry={() => loadDay(currentDate)}
        />
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {entries.length === 0 ? (
            <EmptyState title={t('noEntry')} />
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <EntryBlock
                  key={entry.planningEntryId ?? entry.date}
                  entry={entry}
                  t={t}
                  date={currentDate}
                  checklistsByTaskId={checklistsByTaskId}
                  checklistReadOnly={isFuture}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface EntryBlockProps {
  entry: PlanningEntryListDto;
  t: (key: string, opts?: Record<string, unknown>) => string;
  date: string;
  checklistsByTaskId: Record<string, TaskChecklistGroup>;
  /** Future days show the checklist read-only; past days stay confirmable. */
  checklistReadOnly: boolean;
}

function EntryBlock({ entry, t, date, checklistsByTaskId, checklistReadOnly }: EntryBlockProps) {
  const isAbsence = entry.entryType === 'Absence';
  const isWorkDay = entry.entryType === 'WorkDay';

  if (isAbsence) {
    return (
      <Card>
        <div className="flex items-center gap-3">
          <Badge
            color={
              entry.absenceTypeColor === 'red'
                ? 'red'
                : entry.absenceTypeColor === 'blue'
                  ? 'blue'
                  : 'yellow'
            }
          >
            {t('badges.absence')}
          </Badge>
          <span className="text-[13px] text-slate-700">{entry.absenceTypeName}</span>
        </div>
      </Card>
    );
  }

  if (!isWorkDay) {
    return <EmptyState title={t('noEntry')} />;
  }

  const tasks = (entry.tasks ?? []).slice().sort(
    (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      {/* Day info row */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1.5">
          <div className="flex items-center gap-3 flex-wrap">
            <Clock className="w-4 h-4 text-slate-400" strokeWidth={2} />
            {entry.startTime && entry.endTime && (
              <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                {t('workTime', {
                  start: formatTime(entry.startTime),
                  end: formatTime(entry.endTime),
                })}
              </span>
            )}
            {entry.plannedLicensePlate && (
              <span
                className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 font-mono text-[12px] text-slate-700"
                title={t('plannedVehicle', { plate: entry.plannedLicensePlate })}
              >
                <Car className="w-3.5 h-3.5" aria-hidden />
                {entry.plannedLicensePlate}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 text-[12px]">
            {entry.targetHours != null && (
              <div className="flex items-baseline gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {t('fields.targetHours')}
                </span>
                <span className="tabular-nums text-slate-700">
                  {t('hoursLabel', { hours: entry.targetHours.toFixed(2) })}
                </span>
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {t('fields.plannedHours')}
              </span>
              <span className="tabular-nums font-medium text-[#00A871]">
                {t('hoursLabel', { hours: (entry.plannedHours ?? 0).toFixed(2) })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks */}
      {tasks.length === 0 ? (
        <div className="p-4">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-10">{t('table.nr')}</TableHeader>
              <TableHeader>{t('table.object')}</TableHeader>
              <TableHeader>{t('table.activity')}</TableHeader>
              <TableHeader className="hidden sm:table-cell w-20">{t('table.start')}</TableHeader>
              <TableHeader className="hidden sm:table-cell w-20">{t('table.end')}</TableHeader>
              <TableHeader className="hidden md:table-cell w-24">{t('table.duration')}</TableHeader>
              <TableHeader className="hidden lg:table-cell">{t('table.notes')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task, idx) => (
              <TaskRow
                key={task.planningEntryTaskId ?? `${idx}`}
                task={task}
                idx={idx + 1}
                t={t}
                date={date}
                checklist={task.taskId ? checklistsByTaskId[task.taskId] : undefined}
                checklistReadOnly={checklistReadOnly}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function TaskRow({
  task,
  idx,
  t,
  date,
  checklist,
  checklistReadOnly,
}: {
  task: PlanningEntryTaskDto;
  idx: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
  date: string;
  checklist?: TaskChecklistGroup;
  checklistReadOnly: boolean;
}) {
  const objectLabel = task.objectShortName || task.objectName || '–';
  const activity = task.taskName || task.serviceName || '–';
  const openItems = (checklist?.items ?? []).filter((i) => !i.isCompleted).length;
  const [expanded, setExpanded] = useState(false);
  return (
    <TableRow>
      <TableCell className="text-slate-500 tabular-nums">{task.sequenceOrder ?? idx}</TableCell>
      <TableCell>
        <div className="text-[13px] font-medium text-slate-900">{objectLabel}</div>
        {task.objectName && task.objectShortName && task.objectName !== task.objectShortName && (
          <div className="text-[11px] text-slate-500 truncate">{task.objectName}</div>
        )}
      </TableCell>
      <TableCell>
        <div className="text-[13px] text-slate-900">{activity}</div>
        {task.serviceName && task.taskName && task.serviceName !== task.taskName && (
          <div className="text-[11px] text-slate-500 truncate">{task.serviceName}</div>
        )}
        {/* Mobile: condensed extras */}
        <div className="sm:hidden text-[11px] text-slate-500 tabular-nums mt-0.5">
          {formatTime(task.startTime)}–{formatTime(task.endTime)} · {task.durationMinutes ?? 0} min
        </div>

        {checklist && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
            >
              {expanded ? (
                <ChevronDown className="w-3 h-3" strokeWidth={2} />
              ) : (
                <ChevronRight className="w-3 h-3" strokeWidth={2} />
              )}
              <ListChecks className="w-3 h-3" strokeWidth={2} />
              {openItems > 0
                ? t('checklist.openCount', { count: openItems })
                : t('checklist.allDone')}
            </button>
            {expanded && (
              <TaskChecklistPanel date={date} group={checklist} readOnly={checklistReadOnly} />
            )}
          </>
        )}
      </TableCell>
      <TableCell className="hidden sm:table-cell text-[13px] text-slate-700 tabular-nums">
        {formatTime(task.startTime)}
      </TableCell>
      <TableCell className="hidden sm:table-cell text-[13px] text-slate-700 tabular-nums">
        {formatTime(task.endTime)}
      </TableCell>
      <TableCell className="hidden md:table-cell text-[13px] text-slate-700 tabular-nums">
        {t('durationMinutes', { count: task.durationMinutes ?? 0, defaultValue: `${task.durationMinutes ?? 0} min` })}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-[12px] text-slate-500 whitespace-pre-wrap">
        {task.notes || ''}
      </TableCell>
    </TableRow>
  );
}
